import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdfjs } from '../../signature/pdfjs';
import type { PdfPageSize } from './pdf-placement';

export type PdfDocumentErrorReason = 'password' | 'invalid' | 'timeout';

type PdfDocumentState =
  | { status: 'idle' | 'loading'; doc?: undefined; pages: PdfPageSize[]; errorReason?: undefined }
  | { status: 'ready'; doc: PDFDocumentProxy; pages: PdfPageSize[]; errorReason?: undefined }
  | { status: 'error'; doc?: undefined; pages: PdfPageSize[]; errorReason: PdfDocumentErrorReason };

const LOAD_TIMEOUT_MS = 30_000;
const IDLE_STATE: PdfDocumentState = { status: 'idle', pages: [] };

class PdfLoadTimeoutError extends Error {
  constructor() {
    super('PDF load timed out');
    this.name = 'PdfLoadTimeoutError';
  }
}

interface UsePdfDocumentOptions {
  onError?: (error: unknown, reason: PdfDocumentErrorReason) => void;
}

/** Parses `blob` with pdf.js and reads each page's visual size. The document is destroyed on change/unmount. */
export const usePdfDocument = (blob: Blob | undefined, { onError }: UsePdfDocumentOptions = {}) => {
  const [state, setState] = useState<PdfDocumentState>(IDLE_STATE);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!blob) {
      setState(IDLE_STATE);
      return;
    }

    let cancelled = false;
    let destroy: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    setState({ status: 'loading', pages: [] });

    const load = async () => {
      const [pdfjs, buffer] = await Promise.all([loadPdfjs(), blob.arrayBuffer()]);
      if (cancelled) return;
      // `data` is transferred to the worker, so this buffer is detached afterwards.
      const task = pdfjs.getDocument({ data: new Uint8Array(buffer) });
      destroy = () => void task.destroy();

      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new PdfLoadTimeoutError()), LOAD_TIMEOUT_MS);
      });
      const doc = await Promise.race([task.promise, timeout]);
      const pages = await Promise.race([
        Promise.all(
          Array.from({ length: doc.numPages }, async (_, index) => {
            const page = await doc.getPage(index + 1);
            const { width, height } = page.getViewport({ scale: 1 });
            return { width, height };
          })
        ),
        timeout,
      ]);
      clearTimeout(timeoutId);
      if (!cancelled) setState({ status: 'ready', doc, pages });
    };

    load().catch((error: unknown) => {
      clearTimeout(timeoutId);
      if (cancelled) return;
      destroy?.();
      const errorReason: PdfDocumentErrorReason =
        error instanceof PdfLoadTimeoutError
          ? 'timeout'
          : (error as { name?: string })?.name === 'PasswordException'
          ? 'password'
          : 'invalid';
      setState({ status: 'error', pages: [], errorReason });
      onErrorRef.current?.(error, errorReason);
    });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      destroy?.();
    };
  }, [blob]);

  return state;
};
